import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  CheckCircle,
  Warning,
  Refresh,
  Assignment,
  Timeline,
  Map as MapIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { obtenerEntidades, obtenerDepartamentos } from '../actions/EntidadAction';
import workflowApi from '../api/workflowApi';
import useStyles from '../theme/useStyles';

const DashboardAnalitico = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [entidades, setEntidades] = useState([]);
  const [estadosWorkflow, setEstadosWorkflow] = useState({});
  const [departamentos, setDepartamentos] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Estados del workflow (mismo que en las otras pÃ¡ginas)
  const WORKFLOW_STATES = {
    INICIADO: { 
      id: 1, 
      codigo: 'INICIADO',
      label: 'Proceso Iniciado', 
      color: '#3498db',
      descripcion: 'Workflow iniciado - pendiente registro completo'
    },
    PENDIENTE_REGISTRO: { 
      id: 2, 
      codigo: 'PENDIENTE_REGISTRO',
      label: 'Pendiente Registro', 
      color: '#f39c12',
      descripcion: 'Esperando registro de entidad, usuarios y contactos'
    },
    REGISTRO_COMPLETADO: { 
      id: 3, 
      codigo: 'REGISTRO_COMPLETADO',
      label: 'Registro Completado', 
      color: '#2ecc71',
      descripcion: 'Entidad, usuarios y contactos registrados correctamente'
    },
    DOCUMENTOS_REVISION: { 
      id: 4, 
      codigo: 'DOCUMENTOS_REVISION',
      label: 'Documentos en Revisión', 
      color: '#9b59b6',
      descripcion: 'Documentos siendo revisados por auditor'
    },
    APROBADO: { 
      id: 5, 
      codigo: 'APROBADO',
      label: 'Aprobado', 
      color: '#27ae60',
      descripcion: 'Inscripción aprobada'
    },
    RECHAZADO: { 
      id: 6, 
      codigo: 'RECHAZADO',
      label: 'Rechazado', 
      color: '#e74c3c',
      descripcion: 'Inscripción rechazada'
    },
    PENDIENTE_DOCUMENTOS: { 
      id: 7, 
      codigo: 'PENDIENTE_DOCUMENTOS',
      label: 'Pendiente Documentos', 
      color: '#e67e22',
      descripcion: 'Esperando carga de documentos requeridos'
    },
    DOCUMENTOS_CARGADOS: { 
      id: 8, 
      codigo: 'DOCUMENTOS_CARGADOS',
      label: 'Documentos Cargados', 
      color: '#9b59b6',
      descripcion: 'Todos los documentos han sido cargados'
    },
    EN_REVISION_ADMIN: { 
      id: 9, 
      codigo: 'EN_REVISION_ADMIN',
      label: 'En Revisión Admin', 
      color: '#34495e',
      descripcion: 'Documentos siendo revisados por administrador'
    },
    NO_INICIADO: {
      id: 0,
      codigo: 'NO_INICIADO',
      label: 'No Iniciado',
      color: '#95a5a6',
      descripcion: 'Workflow no iniciado'
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []);

  const mapearEstadoWorkflow = (estadoData) => {
    if (!estadoData) return WORKFLOW_STATES.NO_INICIADO;
    
    const estadoId = estadoData.estadoNegocioId || estadoData.estadoId || estadoData.id;
    if (estadoId) {
      const estadoIdInt = parseInt(estadoId);
      switch (estadoIdInt) {
        case 1: return WORKFLOW_STATES.INICIADO;
        case 2: return WORKFLOW_STATES.PENDIENTE_REGISTRO;
        case 3: return WORKFLOW_STATES.REGISTRO_COMPLETADO;
        case 4: return WORKFLOW_STATES.DOCUMENTOS_REVISION;
        case 5: return WORKFLOW_STATES.APROBADO;
        case 6: return WORKFLOW_STATES.RECHAZADO;
        case 7: return WORKFLOW_STATES.PENDIENTE_DOCUMENTOS;
        case 8: return WORKFLOW_STATES.DOCUMENTOS_CARGADOS;
        case 9: return WORKFLOW_STATES.EN_REVISION_ADMIN;
        default: return WORKFLOW_STATES.NO_INICIADO;
      }
    }
    
    return WORKFLOW_STATES.NO_INICIADO;
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar entidades
      const entidadesResponse = await obtenerEntidades();
      if (entidadesResponse.status === 200) {
        const municipios = entidadesResponse.data.filter(e => e.tipoEntidadID === 1);
        setEntidades(municipios);
        
        // Cargar estados del workflow
        await cargarEstadosWorkflow(municipios);
      }

      // Cargar departamentos
      const deptosResponse = await obtenerDepartamentos();
      if (deptosResponse.status === 200) {
        setDepartamentos(deptosResponse.data);
      }

    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadosWorkflow = async (municipios) => {
    const estadosPromises = municipios.map(async (municipio) => {
      try {
        const entidadId = municipio.entidadID || municipio.id;
        const response = await workflowApi.obtenerEstadoInscripcion(entidadId);
        
        if (response.status === 200 && response.data) {
          const estadoMapeado = mapearEstadoWorkflow(response.data.estado);
          return { entidadId, estado: estadoMapeado };
        }
      } catch (error) {
        return {
          entidadId: municipio.entidadID || municipio.id,
          estado: WORKFLOW_STATES.NO_INICIADO
        };
      }
      return null;
    });

    const resultados = await Promise.all(estadosPromises);
    const estadosMap = {};
    
    resultados.forEach(resultado => {
      if (resultado) {
        estadosMap[resultado.entidadId] = resultado.estado;
      }
    });
    
    setEstadosWorkflow(estadosMap);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await cargarDatos();
    setRefreshing(false);
  };

  // Calcular estadÃ­sticas
  const calcularEstadisticas = () => {
    const total = entidades.length;
    const estadosCount = {};
    
    Object.values(WORKFLOW_STATES).forEach(estado => {
      estadosCount[estado.id] = 0;
    });

    Object.values(estadosWorkflow).forEach(estado => {
      if (estadosCount[estado.id] !== undefined) {
        estadosCount[estado.id]++;
      }
    });

    const aprobados = estadosCount[5];
    const rechazados = estadosCount[6];
    const enProceso = total - aprobados - rechazados;
    const tasaExito = total > 0 ? ((aprobados / total) * 100).toFixed(1) : 0;

    return {
      total,
      aprobados,
      rechazados,
      enProceso,
      tasaExito,
      estadosCount
    };
  };

  // Preparar datos para grÃ¡ficos
  const prepararDatosGraficos = () => {
    const stats = calcularEstadisticas();
    
    // Datos para grÃ¡fico de pie (estados)
    const pieData = Object.values(WORKFLOW_STATES)
      .filter(estado => stats.estadosCount[estado.id] > 0)
      .map(estado => ({
        name: estado.label,
        value: stats.estadosCount[estado.id],
        color: estado.color
      }));

    // Datos para grÃ¡fico de barras por departamento
    const deptoStats = {};
    entidades.forEach(entidad => {
      const deptoNombre = entidad.nombre.split(' - ')[1] || 'Desconocido';
      if (!deptoStats[deptoNombre]) {
        deptoStats[deptoNombre] = { total: 0, aprobados: 0, enProceso: 0 };
      }
      deptoStats[deptoNombre].total++;
      
      const entidadId = entidad.entidadID || entidad.id;
      const estado = estadosWorkflow[entidadId];
      
      if (estado && estado.id === 5) {
        deptoStats[deptoNombre].aprobados++;
      } else if (estado && estado.id !== 6) {
        deptoStats[deptoNombre].enProceso++;
      }
    });

    const barData = Object.entries(deptoStats)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 10)
      .map(([nombre, data]) => ({
        departamento: nombre.length > 15 ? nombre.substring(0, 15) + '...' : nombre,
        total: data.total,
        aprobados: data.aprobados,
        enProceso: data.enProceso
      }));

    return { pieData, barData };
  };

  const stats = calcularEstadisticas();
  const { pieData, barData } = prepararDatosGraficos();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      p: 2,
      // backgroundColor: '#f5f5f5',
      backgroundColor: 'transparent',
      boxSizing: 'border-box'
    }}>
      <Card className={classes.card}
        sx={{ 
          width: '100%',
          maxWidth: 1400,
          margin: 'auto',
          mt: 4,
          p: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.35)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.38)',
          borderRadius: '20px'
        }}
      >
        <CardHeader 
          avatar={
            <DashboardIcon style={{ fontSize: "40px", color: "#1E88E5" }} />
          }
          title={
            <Typography variant="h4" fontWeight="bold" color="#1E88E5" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Dashboard Analítico
            </Typography>
          }
          subheader="Seguimiento del Proceso de Inscripción de Municipios"
          action={
            <Grid container spacing={1} alignItems="center" justifyContent="center">
              <Grid item>
                <Tooltip title="Ver Mapa" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={() => navigate('/mapa')}
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.button}
                  >
                    <MapIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Gestión de Entidades" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={() => navigate('/entidades')}
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={classes.button}
                  >
                    <Assignment />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Actualizar datos" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    color="primary"
                    size="small"
                  >
                    <Refresh />
                    {refreshing && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          }
          sx={{ 
            '& .MuiCardHeader-title': { fontSize: '1.25rem' },
            pr: 6
          }}
        />
        
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          {/* Cards de mÃ©tricas principales - CENTRADAS */}
          <Box sx={{ mb: 3 }}>
            {/* <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Métricas Principales
            </Typography> */}
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={2} sx={{ maxWidth: 1000 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '16px', border: '2px solid #1E88E5' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography color="text.secondary" variant="body2" gutterBottom>
                            Total Municipios
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                            {stats.total}
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            En el sistema
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 48, height: 48 }}>
                          <People />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '16px', border: '2px solid #4CAF50' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography color="text.secondary" variant="body2" gutterBottom>
                            Aprobados
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ mb: 1 }}>
                            {stats.aprobados}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stats.tasaExito}% tasa de éxito
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', width: 48, height: 48 }}>
                          <CheckCircle />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '16px', border: '2px solid #FF9800' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography color="text.secondary" variant="body2" gutterBottom>
                            En Proceso
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ mb: 1 }}>
                            {stats.enProceso}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {((stats.enProceso / stats.total) * 100).toFixed(1)}% del total
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main', width: 48, height: 48 }}>
                          <Timeline />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: '16px', border: '2px solid #E63946' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography color="text.secondary" variant="body2" gutterBottom>
                            Rechazados
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color="error.main" sx={{ mb: 1 }}>
                            {stats.rechazados}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {((stats.rechazados / stats.total) * 100).toFixed(1)}% del total
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'error.light', color: 'error.main', width: 48, height: 48 }}>
                          <Warning />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* GrÃ¡ficos - CADA UNO OCUPA 50% */}
          <Box sx={{ mb: 3 }}>
            {/* <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              AnÃ¡lisis GrÃ¡fico
            </Typography> */}
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={3} sx={{ maxWidth: 1200 }}>
                {/* GrÃ¡fico de Pie - 50% del ancho */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={1} sx={{ p: 3, height: '450px' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Distribución por Estado del Workflow
                    </Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                          outerRadius="70%"
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          formatter={(value, name) => [value, name]}
                          contentStyle={{ fontSize: '14px' }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico de Barras - 50% del ancho */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={1} sx={{ p: 3, height: '450px' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Top 10 Departamentos con más Municipios
                    </Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart 
                        data={barData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="departamento" 
                          angle={-45} 
                          textAnchor="end" 
                          height={100}
                          interval={0}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <RechartsTooltip contentStyle={{ fontSize: '14px' }} />
                        <Legend 
                          verticalAlign="top" 
                          height={36}
                          wrapperStyle={{ fontSize: '12px' }}
                        />
                        <Bar dataKey="aprobados" fill="#27ae60" name="Aprobados" />
                        <Bar dataKey="enProceso" fill="#f39c12" name="En Proceso" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Detalles por Estado */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Desglose Detallado por Estado
            </Typography>
            
            <Paper elevation={1} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {Object.values(WORKFLOW_STATES).map(estado => {
                  const count = stats.estadosCount[estado.id] || 0;
                  const porcentaje = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : 0;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={estado.id}>
                      <Box sx={{ 
                        p: 2, 
                        border: `1px solid ${estado.color}30`,
                        borderLeft: `4px solid ${estado.color}`,
                        borderRadius: 1,
                        bgcolor: 'background.paper'
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" fontWeight="600">
                            {estado.label}
                          </Typography>
                          <Chip 
                            label={count} 
                            size="small" 
                            sx={{ 
                              bgcolor: estado.color, 
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={parseFloat(porcentaje)} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: `${estado.color}20`,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: estado.color
                            }
                          }} 
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {porcentaje}% del total
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardAnalitico;